/**
 * insuranceSummary.ts — the proof-read, one moment before the policy exists.
 *
 * NOT "ARE YOU SURE?". Every particular the instrument states, in the instrument's own order and
 * under the instrument's own row labels, as it will print — so a fat-fingered weekly recovery reads
 * as an absurd line rather than as a right-looking number in a form. `advanceSummary`'s surface over
 * a year of cover, and deliberately its shape: the clause/part vocabulary is imported from the
 * contract's activation summary rather than restated, so the confirm dialog this feeds is the one
 * `ActivateGate` already draws.
 *
 * AND THERE ARE TWO INSTRUMENTS, WHICH IS THE WHOLE DIFFERENCE FROM THE ADVANCE'S [D-B45]. A year
 * Primelane financed prints the Insurance Premium Advance's money grid — premium, what the Borrower
 * paid, the difference, interest, total, weekly, first day [D-B46]. A year the driver paid for in
 * full prints the Insurance Premium Payment Record's, which advances nothing and charges nothing.
 * Proof-reading one against the other's rows would put four permanently empty lines in front of an
 * admin as work still to do, and would name a document that is not going to be generated.
 *
 * AND THE ADVANCE HAS TWO REPAYMENT ARRANGEMENTS, WHICH IS A THIRD SET OF ROWS AND NOT A THIRD
 * DOCUMENT NAME [D-B57]. An advance with no weekly recovery, on a contract whose hire purchase
 * agreement leaves no room for one, is settled at the end of the contract — so the grid states
 * that in one row where the pair would have been. `ADVANCE_HEADING` does not move: the page is an
 * Insurance Premium Advance either way, and it is titled so.
 *
 * THE DERIVED ROWS COUNT AS NOTHING MISSING, AND THAT IS THE POINT OF `fields: []`. The amount
 * advanced, the interest and the total are arithmetic off the premium, the deposits and the rate;
 * an admin cannot go and fill any of them in. The Record's own paid rows are the same — they are
 * the deposit link read back, not boxes — so listing them as empty required fields would put "3
 * fields this policy requires are empty" over a dialog whose one actual gap is elsewhere.
 *
 * THE RATE IS SHOWN AS IT WOULD BE WRITTEN. An empty rate box writes NULL and a covering deposit
 * writes 0, and both print as 0 per cent on a page that charges nothing — so the proof-read prints
 * the figure the document will carry rather than a dash for a row that always renders.
 *
 * PURE, AND REACT-FREE.
 */

import { format } from 'date-fns';

import { INSTRUMENT_TITLES } from '@/lib/agreements/instrument';
import { businessDay, formatCurrency } from '@/lib/format';

import type { ClausePart, SummaryClause } from '../activationSummary';
import { printedMoney } from './insuranceArithmetic';
import { insuranceAreaForField } from './insuranceDeepLink';
import type { InsuranceDraft } from './insuranceForm';
import { writesAtEndAdvance, type InsuranceSnapshot } from './insuranceState';
import type { InsuranceArea } from './insuranceSteps';

export interface InsuranceSummaryFact {
    field: string;
    /** The instrument's own row label. */
    label: string;
    value: string | null;
    area: InsuranceArea;
    /** Filled by the document generator rather than by anybody in this flow. */
    derived?: boolean;
}

export interface InsuranceSummary {
    clauses: SummaryClause[];
    facts: InsuranceSummaryFact[];
    /** The required fields with nothing in them, in reading order. */
    missing: InsuranceSummaryFact[];
    /** The heading over the block, in `INSTRUMENT_HEADINGS`' voice. */
    heading: string;
}

const MISSING = '—';

export const ADVANCE_HEADING = `The ${INSTRUMENT_TITLES.ADVANCE} prints`;
export const RECORD_HEADING = `The ${INSTRUMENT_TITLES.PAYMENT_RECORD} prints`;

function part(value: string | null, prefix = '', suffix = ''): ClausePart {
    if (value === null) return { kind: 'missing', text: MISSING };
    return { kind: 'value', text: `${prefix}${value}${suffix}` };
}

function money(amount: number | null | undefined): string | null {
    return amount === null || amount === undefined ? null : formatCurrency(amount);
}

/** A DATE string on the Ghana calendar day it names — never `new Date(iso)`. */
function dateText(value: string | null | undefined): string | null {
    const date = businessDay(value);
    return date ? format(date, 'MMM d, yyyy') : null;
}

function filled(value: string | null | undefined): string | null {
    return value && value.trim() ? value.trim() : null;
}

/**
 * The rate as the instrument prints it — `FM990.00` with the trailing zeros and the dot trimmed,
 * which is `agreement_merge_data`'s own spelling for `{{insurance_loan.interest_rate}}`. 12 prints
 * "12", 12.5 prints "12.5", and 0 prints "0".
 */
function percentText(rate: number): string {
    return rate.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
}

const FIELD_LABELS: Record<string, string> = {
    'insurance.premium_amount': 'Premium for the insurance period',
    'insurance_loan.weekly_instalment': 'Weekly repayment',
    'insurance_loan.first_instalment_date': 'First repayment',
};

/**
 * What the driver has paid, as the Record's own date row reads it.
 *
 * THE SNAPSHOT'S ROW ONLY FOR THE SAME TRANSACTION, which is `advanceSummary`'s rule for the
 * principal: the picker hands the page an amount but not a date, so the day comes off the deposit
 * row the policy read carries — and only where it is the same payment. On a create there is no such
 * row yet and the date is unknown here, which is exactly why its clause carries no field.
 */
function paidOnOf(draft: InsuranceDraft, snapshot: InsuranceSnapshot): string | null {
    if (draft.depositTransactionId === null) return null;
    const linked = snapshot.deposits.find(
        (deposit) => deposit.transactionId === draft.depositTransactionId,
    );
    return linked?.paidOn ?? null;
}

/**
 * The Advance's money grid, in the template's order [D-B46].
 *
 * THE SUBTRACTION IS STATED RATHER THAN IMPLIED, because v7 states it: the premium, the part the
 * Borrower paid and the difference between them, and then the interest charged on that difference.
 * A borrower checking their Schedule against their bank statement derives every figure; so does an
 * admin reading this dialog beside it.
 */
function advanceClauses(
    draft: InsuranceDraft,
    depositTotal: number,
): SummaryClause[] {
    const premium = draft.premiumAmount;
    /*
     * THE RATE THE SAVE WILL WRITE AND THE PAID FIGURE THE RESOLVER CLAMPS, BOTH FROM ONE AUTHOR
     * [D-B45]. `printedMoney` carries the whole argument for each; what matters here is that the
     * Advance canvas states the same grid while it is being typed, and this dialog is what it is
     * proof-read against — so neither adjustment may be spelled twice.
     */
    const { rate, deposit: paid, principal, interest, total } = printedMoney(
        premium,
        depositTotal,
        draft.interestRate,
    );

    return [
        {
            no: 'Premium for the insurance period',
            fields: ['insurance.premium_amount'],
            parts: [part(money(premium))],
        },
        {
            /**
             * A FIGURE AND NEVER A BLANK. The resolver prints GH₵0.00 where the loan carries no
             * deposit row at all, and most production loans are that shape — a dash here would put
             * "1 field this policy requires is empty" over every ordinary financed year.
             */
            no: 'Paid by the Borrower',
            fields: ['insurance_loan.deposit_amount'],
            parts: [part(money(paid))],
        },
        {
            // ARITHMETIC, SO NO FIELD OF ITS OWN — the premium less what the Borrower paid.
            no: 'Amount advanced',
            fields: [],
            parts: [
                part(money(principal)),
                { kind: 'text', text: ' — the Premium less the amount the Borrower paid.' },
            ],
        },
        {
            no: 'Interest',
            fields: [],
            parts: [
                part(money(interest)),
                {
                    kind: 'text',
                    text: `, being ${percentText(rate)} per cent of the Amount advanced, charged once when this Schedule is signed.`,
                },
            ],
        },
        {
            no: 'Total repayable',
            fields: [],
            parts: [
                part(money(total)),
                { kind: 'text', text: ' — the Amount advanced together with that interest.' },
            ],
        },
        {
            no: 'Weekly repayment',
            fields: ['insurance_loan.weekly_instalment'],
            parts: [part(money(draft.weeklyAmount))],
        },
        {
            no: 'First repayment',
            fields: ['insurance_loan.first_instalment_date'],
            parts: [
                part(dateText(draft.firstStartsAt)),
                {
                    kind: 'text',
                    text: ', and every Monday after it until the Total repayable is repaid in full.',
                },
            ],
        },
    ];
}

/**
 * The two rows an advance settled at the end of the contract does not have [D-B57].
 *
 * NAMED RATHER THAN COUNTED. The pair is last in the grid above, so a slice would do — and would
 * quietly take the wrong rows the first time somebody adds one.
 */
const WEEKLY_ROWS = new Set(['Weekly repayment', 'First repayment']);

/**
 * The Advance's money grid with ONE repayment row in place of the pair [D-B57].
 *
 * `fields: []` IS THE WHOLE OF THE FIX, and it is `Amount advanced`'s own idiom: a row with no
 * merge key of its own cannot land in `missing[]`, so the confirm dialog stops reporting "2 fields
 * this policy requires are empty" over a year whose repayment arrangement is complete. The page
 * this proof-reads states its repayment arrangement in fixed wording, so there is no weekly figure
 * for anybody to supply and none to leave blank.
 *
 * AND THE ROW STATES WHAT THAT PAGE PRINTS, IN ONE LINE WHERE THE PAGE USES TWO [D-B57]. The page
 * keeps a Weekly repayment row — none payable while the Hire-purchase Price is being paid, an
 * amount equal to the weekly instalment after that — and a First repayment row dating that tail
 * from the day the Hire-purchase Price is paid in full. Neither carries a figure anybody supplies,
 * and between them they describe one arrangement, so the grid states it once in the words an admin
 * uses for it. Every other row here is the template's own label.
 *
 * THE FIRST FIVE ROWS ARE THE ADVANCE'S, UNTOUCHED. The premium, what the Borrower paid, the
 * amount advanced, the interest and the total are the same figures on the same arrangement —
 * interest is charged on an at-end advance exactly as it is on any other.
 */
function atEndClauses(draft: InsuranceDraft, depositTotal: number): SummaryClause[] {
    return [
        ...advanceClauses(draft, depositTotal).filter((clause) => !WEEKLY_ROWS.has(clause.no)),
        {
            no: 'Repayment',
            fields: [],
            parts: [
                {
                    kind: 'text',
                    text:
                        'None while the vehicle is being paid for, then the contract instalment ' +
                        'until the advance is repaid.',
                },
            ],
        },
    ];
}

/**
 * The Payment Record's six rows, in the template's order [D-B45].
 *
 * SIX AND NOT THE GRID'S NINE. The three left off — what is owed under the Record, what is payable
 * after it and how the payment is shown in the account — are the Record's PROMISES rather than
 * particulars of this premium: they print the same words over every year, and nothing an admin
 * types here can make any of them wrong. A proof-read is for the figures that could be.
 *
 * THREE OF THE SIX CARRY NO FIELD AT ALL, and they are the rows the template states as fixed
 * wording: the payment was made by the Driver to Primelane, Primelane advanced nothing, and no
 * interest arises. They are on the dialog because an admin is confirming that this is the document
 * about to be generated — a year that should have been an Advance is caught by reading them.
 */
function recordClauses(
    draft: InsuranceDraft,
    snapshot: InsuranceSnapshot,
    depositTotal: number,
): SummaryClause[] {
    const premium = draft.premiumAmount;
    /*
     * CAPPED AT THE PREMIUM, WHICH IS THE RESOLVER'S OWN `LEAST(SUM(deposits), il.amount)`: an
     * overpaid year must not print more paid than the amount stated above it, and this row is
     * proof-read against the document it fills. THE SAME AUTHOR AS THE ADVANCE'S, because it is the
     * same clamp — a Record carries no rate, so the third argument is the absence.
     */
    const paid = printedMoney(premium, depositTotal, null).deposit;

    return [
        {
            no: 'Amount paid for the insurance period',
            fields: ['insurance.premium_amount'],
            parts: [part(money(premium))],
        },
        {
            no: 'Paid by the Driver',
            fields: [],
            parts: [part(money(paid)), { kind: 'text', text: ', in full.' }],
        },
        {
            no: 'Date the premium was paid in full',
            fields: [],
            parts: [part(dateText(paidOnOf(draft, snapshot)))],
        },
        {
            no: 'How the payment was made',
            fields: [],
            parts: [{ kind: 'text', text: 'Paid by the Driver to Primelane.' }],
        },
        {
            no: 'Amount advanced by Primelane',
            fields: [],
            parts: [{ kind: 'text', text: 'Nil — Primelane advanced nothing towards this premium.' }],
        },
        {
            no: 'Interest',
            fields: [],
            parts: [
                { kind: 'text', text: 'None. No interest arises on a premium the Driver paid in full.' },
            ],
        },
    ];
}

/**
 * THE PARTIES AND THE VEHICLE — the instruments' own SEVEN, and the reason they are on a proof-read
 * this flow cannot edit them from: they are render-blocking merge keys, and the last chance anybody
 * has to notice that the wrong person or the wrong car has been named is the moment before the
 * document is generated over them. The block beneath links out to the contract's own driver and
 * vehicle areas, which is where they are fixed.
 *
 * SEVEN AND NOT THE ADVANCE SCHEDULE'S FIVE: both insurance templates print the year of manufacture
 * and the colour in their particulars grid. And the first three are named for the instrument — the
 * Advance's counterparty is the Borrower, the Record's is the Driver, because the Record records a
 * payment rather than a bargain — which is also the namespace the resolver answers them under.
 */
function partyFacts(snapshot: InsuranceSnapshot): InsuranceSummaryFact[] {
    const person = snapshot.instrument === 'PAYMENT_RECORD' ? 'driver' : 'borrower';
    const who = snapshot.instrument === 'PAYMENT_RECORD' ? 'Driver' : 'Borrower';
    const { driver, vehicle } = snapshot;
    const vehicleName = filled([vehicle.make, vehicle.model].filter(Boolean).join(' '));
    const year = vehicle.year === null || vehicle.year === undefined ? null : String(vehicle.year);

    return [
        { field: `${person}.full_name`, label: who, value: filled(driver.fullName), area: 'agreement' },
        {
            field: `${person}.address`,
            label: `${who}’s address`,
            value: filled(driver.address),
            area: 'agreement',
        },
        {
            field: `${person}.phone`,
            label: `${who}’s telephone`,
            value: filled(driver.phone),
            area: 'agreement',
        },
        { field: 'vehicle.model', label: 'Vehicle', value: vehicleName, area: 'agreement' },
        {
            field: 'vehicle.year_of_manufacture',
            label: 'Year of manufacture',
            value: filled(year),
            area: 'agreement',
        },
        { field: 'vehicle.colour', label: 'Colour', value: filled(vehicle.colour), area: 'agreement' },
        {
            field: 'vehicle.registration',
            label: 'Registration number',
            value: filled(vehicle.registration),
            area: 'agreement',
        },
    ];
}

/**
 * The year of cover as its instrument will read it.
 *
 * IT TAKES THE DRAFT rather than the snapshot's saved rows, because the whole purpose of the
 * surface is the figures that are about to be written — and the snapshot for the parties and the
 * instrument, because those are facts about the contract and about the shape this save produces
 * that this module does not decide.
 *
 * THE DEPOSIT TOTAL IS PASSED IN RATHER THAN SUMMED HERE. `draftDepositTotal` is its one author, it
 * reads the FORM's strings, and it carries the deposit rows this screen does not edit — a second
 * sum taken off the parsed draft alone would print an Advance's principal over a premium the driver
 * has in fact settled, on the one surface that exists to be read against the document.
 */
export function insuranceSummary(
    draft: InsuranceDraft,
    snapshot: InsuranceSnapshot,
    depositTotal: number,
): InsuranceSummary {
    const isRecord = snapshot.instrument === 'PAYMENT_RECORD';
    /*
     * THE THIRD ROUTE IS DRAFT-SHAPED LIKE THE OTHER TWO [D-B57]. `writesAtEndAdvance` asks the
     * snapshot what THIS SAVE would publish — a paper hire purchase agreement, no Payment Record
     * and no weekly recovery written — which is the question this surface has always asked: the
     * rows are what the document about to be generated will print, not what the junction carries
     * today. The rail's own predicate asks one more thing, and says there why.
     */
    const clauses = isRecord
        ? recordClauses(draft, snapshot, depositTotal)
        : writesAtEndAdvance(snapshot)
            ? atEndClauses(draft, depositTotal)
            : advanceClauses(draft, depositTotal);

    const facts = partyFacts(snapshot);

    /*
     * WHICH CANVAS TO SEND AN ADMIN TO IS ASKED OF THE DEEP-LINK RESOLVER AND ANSWERED NOWHERE ELSE.
     * A proof-read naming an empty field is a surface whose whole job is saying where to go, and the
     * pre-flight beside it asks the same question of the same keys — two tables would be two answers
     * for `insurance.premium_amount`, which `agreement_merge_data` resolves from the LOAN and this
     * dossier therefore types on the Advance canvas.
     */
    const missingClauseFields: InsuranceSummaryFact[] = [];
    for (const clause of clauses) {
        if (!clause.parts.some((piece) => piece.kind === 'missing')) continue;
        for (const field of clause.fields) {
            missingClauseFields.push({
                field,
                label: FIELD_LABELS[field] ?? clause.no,
                value: null,
                area: insuranceAreaForField(field),
            });
        }
    }

    const missing = [
        ...missingClauseFields,
        ...facts.filter((fact) => !fact.derived && fact.value === null),
    ];

    return { clauses, facts, missing, heading: isRecord ? RECORD_HEADING : ADVANCE_HEADING };
}
